import * as $ from 'jquery';
import { IDisposable, IEventAggregator, disposableSubscription, serialDisposable } from 'common/barrels/core';
import { Observable, AsyncSubject, ISubscription } from 'common/barrels/rx';
import { Serializer } from 'common/serializer';
import { IResourceProvider } from 'common/resourceprovider';
import { Request } from 'common/request';
import { SystemError } from 'common/exceptions/systemerror';
import { dateTimeTransmissionFormat, defaultShortDateTimeFormat } from 'common/functions/date';

import { ViewScreen, ViewScreenType, ViewResultType, PageChangeSource, IViewLoadPageArgs } from 'runtime/screens/viewscreen';
import { Control, IViewModelRoot } from 'runtime/controls/control';
import { NumericBox } from 'runtime/controls/numericbox';
import { WorkTrayView } from 'runtime/data/worktrayview';
import { WorkItemInfo } from 'runtime/data/workiteminfo';
import { WorkflowInstanceInfo } from 'runtime/data/workflowinstanceinfo';
import { WorkflowInstanceInfoPage } from 'runtime/data/workflowinstanceinfopage';
import { SearchResult } from 'runtime/data/searchresult';
import { SearchResultPage } from 'runtime/data/searchresultpage';
import { LookupItem } from 'runtime/data/lookupitem';
import { UnsupportedDate, UnsupportedWildcardLocation } from 'runtime/messages/workitemsearch';

import * as rxui from 'common/rxui/core';
import * as alerts from 'common/dom/alerts';
import * as path from 'shared/path';
import * as moment from 'moment'
import * as ko from 'knockout';
import * as komapping from 'knockout.mapping';

import 'runtime/styles/screens/search.less';

interface ISearchCriteria {
    searchQuery: string;

    fromDate: string;
    toDate: string;

    version: string;
}

class SearchEvent {
    eventName = SearchEvent.eventName;
    static eventName() { return 'Search.Search'; }
}

export class Search extends ViewScreen {
    private vm: SearchViewModel;
    private $searchTips: JQuery;
    private searchDisposable: IDisposable;
    private currentSearchDisposable = serialDisposable(null);
    private lastSearchQuery: string;

    constructor($hostElement: JQuery, resourcesObservable: Observable<IResourceProvider>, parentEventAggregator: IEventAggregator, serializer: Serializer, view: string, searchParams: string, csrfToken: () => string) {
        super($hostElement, resourcesObservable, parentEventAggregator, ViewScreenType.Search, serializer, csrfToken);

        var asyncSubject = new AsyncSubject<WorkTrayView>();
        var asyncSubjectSubscription: ISubscription = null;
        asyncSubjectSubscription = Observable.zip(this.viewAvailable, asyncSubject, (v1, v2) => v2).subscribe(value => {
            this.initializeSearch(value, disposableSubscription(asyncSubject, asyncSubjectSubscription));
            this.InitializeViewModelAndPerformSearch(searchParams);
        });

        Request.create(serializer, path.api(`search/${view}`))
            .handle(WorkTrayView, value => {
                asyncSubject.next(value);
                asyncSubject.complete();
            })
            .handle(SystemError, () => this.systemError('Search load failed: System Error'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Search load failed: ${errorThrown}`, textStatus))
            .get();
    }

    dispose(): void {
        $(document).off('.wfSearchTips');
        this.currentSearchDisposable.dispose();
        this.searchDisposable.dispose();
        super.dispose();
    }

    remove(): void {
        this.$element.remove();
        this.dispose();
    }

    private InitializeViewModelAndPerformSearch(searchParams: string): void {
        const defaultDateTimeFormat = defaultShortDateTimeFormat();
        const params = new URLSearchParams(searchParams);
        const searchQuery = params.get('query');
        const fromDate = convertToDateTime(params.get('fromDate'));
        const toDate = convertToDateTime(params.get('toDate'));
        const version = this.vm.getVersion(params.get('version'));
        
        this.vm.searchQuery(searchQuery);
        this.vm.fromDate(fromDate);
        this.vm.toDate(toDate);
        this.vm.version(version?.keyValue);
        this.performSearch();
            
        function convertToDateTime(dateString: string): string {
            return moment(dateString, defaultDateTimeFormat).isValid() ? moment(dateString).format(dateValueFormat) : null;
        }
    }

    private initializeSearch(view: WorkTrayView, constructorDisposable: IDisposable): void {
        this.viewId.next(view.id);
        this.view.next(view);

        this.buildSearchContainer(this.$element, this.resources);

        Control.instance($('.wf-page-index .numericbox')[0])['content.Value'](1);
        Control.instance($('.wf-page-index .numericbox')[0])['content.Value'](0);
        var pageSizeControl = <NumericBox>Control.instance($('.wf-page-size .numericbox')[0]);
        pageSizeControl['content.Value'](0);
        pageSizeControl['content.Value'](this.defaultPageSize);
        pageSizeControl.setAdditionalValidator((raw, dec) => raw !== null && dec.greaterThan(0));

        var vm = this.vm = new SearchViewModel(this.eventAggregator, this.resources);

        ko.applyBindings(vm, this.$element[0]);

        this.applySearchQueryPlaceholder(this.$element, this.resources);

        var searchTypeInfo = this.determineSearchTypeInfo(vm.version());
        var viewResultType = searchTypeInfo.isWorkflowStatusSearch ? ViewResultType.SearchWorkflowStatus : ViewResultType.SearchWorkItems;
        this.paintViewHeader(view, viewResultType);

        this.searchDisposable = disposableSubscription(
            this.viewScreenDisposable,
            constructorDisposable,

            this.eventAggregator.query(SearchEvent).subscribe(() => this.performSearch()),

            this.subscribeToItemActions(),
            this.subscribeToSubjects()
        );

        this.viewInitialized = true;
    }

    private applySearchQueryPlaceholder($element: JQuery, resources: IResourceProvider): void {
        $('.wf-search-searchquery input', $element).attr('placeholder', resources.getCoreString('WorkItemSearchPlaceholder'));
    }

    private buildSearchContainer($element: JQuery, resources: IResourceProvider): void {
        $('.wf-header', this.$element).append([
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlSearchText')),
                $('<div />').addClass('wf-filter-control').append([
                    $('<div />').addClass('wf-search-searchquery')
                        // delegated: the input is only created once ko.applyBindings runs
                        .on('keydown', 'input', (e: JQueryEventObject) => { if ((e as any).key === 'Enter') this.vm.search(); })
                        .append([
                            $('<div />').attr('data-bind', 'textBox: { value: searchQuery }')
                        ]),
                    $('<div />').addClass('wf-filter-text').append(
                        $('<a />').addClass('wf-search-tips-toggle').attr('href', '#')
                            .text(resources.getCoreString('WorkItemSearchSyntaxHintTitle'))
                            .on('click', e => { e.preventDefault(); this.toggleSearchTips(); })
                    )
                ])
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlDateFrom')),
                $('<div />').addClass('wf-filter-control').append([
                    $('<div />').addClass('wf-search-date').append([
                        $('<div />').attr('data-bind', 'dateTimePicker: { selectedDate: fromDate }')
                    ])
                ])
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlDateTo')),
                $('<div />').addClass('wf-filter-control').append([
                    $('<div />').addClass('wf-search-date').append([
                        $('<div />').attr('data-bind', 'dateTimePicker: { selectedDate: toDate }')
                    ])
                ])
            ]),
            $('<div />').addClass('wf-filter').append([
                $('<div />').addClass('wf-filter-label').text(resources.getCoreString('XamlVersions')),
                $('<div />').addClass('wf-filter-control').append(
                    $('<div />').addClass('wf-search-versions').append([
                        $('<div />').attr('data-bind', 'dropDownList: { selectedValue: version, context: \'search.version\', lookupItems: getVersions, lookupItem: getVersion }')
                    ])
                )
            ]),
            $('<div />').addClass('wf-filter').append(
                $('<div />').addClass('wf-filter-label').append([
                    $('<div />').addClass('wf-filter-reset').append([
                        $('<div />').attr('data-bind', `activityButton: { click: reset, clickBubble: false, text: \'${resources.getCoreString('XamlReset')}\' }`)
                    ]),
                    $('<div />').addClass('wf-filter-search').css('float', 'right').append([
                        $('<div />').attr('data-bind', `activityButton: { click: search, clickBubble: false, text: \'${resources.getCoreString('XamlSearch')}\' }`)
                    ])
                ])
            ),
            this.buildSearchTips(resources)
        ]);
    }

    private buildSearchTips(resources: IResourceProvider): JQuery {
        // one resource string so translators keep the table together: rows split on '~', each row is 'example | meaning'
        const rows = resources.getCoreString('WorkItemSearchSyntaxHint').split('~')
            .map(row => {
                const pipe = row.indexOf('|');
                return pipe < 0 ? [row, ''] : [row.slice(0, pipe), row.slice(pipe + 1)];
            })
            .map(([example, meaning]) => $('<tr />').append([
                $('<td />').addClass('wf-search-syntax-hint-example').text(example.trim()),
                $('<td />').text(meaning.trim())
            ]));

        this.$searchTips = $('<div />').addClass('wf-search-syntax-hint').append([
            $('<div />').addClass('wf-search-syntax-hint-title').text(resources.getCoreString('WorkItemSearchSyntaxHintTitle')),
            $('<table />').append(rows)
        ]);

        return this.$searchTips;
    }

    private toggleSearchTips(show?: boolean): void {
        const open = show === undefined ? !this.$searchTips.hasClass('wf-open') : show;
        this.$searchTips.toggleClass('wf-open', open);

        $(document).off('.wfSearchTips');
        if (!open)
            return;

        // clicking into the search box keeps the tips open: that is when they are being read
        const $inside = this.$searchTips.add(this.$searchTips.closest('.wf-header').find('.wf-search-tips-toggle, .wf-search-searchquery'));
        $(document)
            .on('keydown.wfSearchTips', (e: JQueryEventObject) => { if ((e as any).key === 'Escape') this.toggleSearchTips(false); })
            .on('mousedown.wfSearchTips', (e: JQueryEventObject) => { if (!$inside.is(e.target) && !$inside.has(e.target).length) this.toggleSearchTips(false); });
    }

    private subscribeToItemActions(): ISubscription {
        var $table = $('.wf-view-table-inner > table', this.$element);

        return rxui.click($table).subscribe(mouseEvent => {
            var $target = $(mouseEvent.target);

            var $menuItem = $target.closest('.wf-item-menu-item');

            if ($menuItem === undefined || $menuItem === null || $menuItem.length === 0) {
                return;
            }

            mouseEvent.stopPropagation();

            var action = $menuItem.data('itemAction');

            var $tr = $target.closest('tr');

            switch (action) {

                case 'showHistory':
                    var item: WorkflowInstanceInfo = $tr.data('key');
                    this.showHistory($tr, item.workflowInstanceId);
                    $menuItem.find('i').removeClass('fa-plus').addClass('fa-minus');
                    $menuItem.data('itemAction', 'hideHistory');
                    break;

                case 'hideHistory':
                    $menuItem.data('itemAction', 'showHistory');
                    $menuItem.find('i').removeClass('fa-minus').addClass('fa-plus');
                    var $historyTr = $tr.next('.wf-item-history');
                    $historyTr.next('.wf-item-history').remove();
                    $historyTr.remove();
                    break;

                case 'edit':
                    var editInfo: SearchResult | WorkItemInfo = $tr.data('key');
                    this.openEdit(editInfo.workItemId);
                    break;

                case 'view':
                    var viewInfo: SearchResult | WorkItemInfo = $tr.data('key');
                    this.openRead(viewInfo.workItemId, this.view.getValue().viewDataPreference);
                    break;
            }
        });
    }

    private subscribeToSubjects(): ISubscription {
        return Observable.merge<PageChangeSource>(this.pageSizeSource, this.pageIndexSource)
            .skipWhile(() => !this.viewInitialized)
            .skip(1) // Skip the first emission that occurs during initialization
            .filter(() => !this.viewUpdating)
            .subscribe(
            value => {
                this.updating();

                var args: IViewLoadPageArgs = {
                    viewId: this.view.getValue().id,
                    pageSize: value.pageSize || this.pageSize.getValue(),
                    pageIndex: value.pageIndex || this.pageIndex.getValue(),
                    sortDescriptions: this.sortDescriptions.getValue()
                };

                this.loadPage(args, () => this.notUpdating());
            },
            // this should never execute as the subscriptions are to behavior subjects of primitive types
            (exception: any) => {
                console.log(JSON.stringify(exception));
                alert('ERROR.  Check console');
            }
        );
    }

    private determineSearchTypeInfo(version: string): { isWorkflowStatusSearch: boolean; latestOnly: boolean } {
        var isWorkflowStatusSearch = (version === 'StatusLatest' || version === 'StatusAllVersions');

        return {
            isWorkflowStatusSearch: isWorkflowStatusSearch,
            latestOnly: (version === (isWorkflowStatusSearch ? 'StatusLatest' : 'LatestVersionOnly'))
        };
    }

    private loadPage(args: IViewLoadPageArgs, final: () => void): IDisposable {
        var criteria = this.vm.getCriteria();
        // the banner names the query that was sent, not whatever is in the box when the page lands;
        // hide it before the request so a failed one never leaves the previous banner behind
        this.lastSearchQuery = criteria.searchQuery;
        this.showNoResults(false);

        var searchTypeInfo = this.determineSearchTypeInfo(criteria.version);

        var requestArgs: any = {
            pageSize: args.pageSize,
            pageIndex: args.pageIndex
        };
        if (args.sortDescriptions !== undefined && args.sortDescriptions !== null && args.sortDescriptions.length > 0) {
            requestArgs.sortDescriptions = args.sortDescriptions;
        }
        requestArgs.criteria = {
            fromDate: criteria.fromDate,
            toDate: criteria.toDate,
            latestOnly: searchTypeInfo.latestOnly,
            searchQuery: criteria.searchQuery
        };

        var webAction = searchTypeInfo.isWorkflowStatusSearch ? 'workflowstatus' : 'workitems';

        return Request.create(this.serializer, path.api(`search/${webAction}/${args.viewId}`))
            .args(() => requestArgs)
            .handle(SearchResultPage, page => this.page.next(page))
            .handle(WorkflowInstanceInfoPage, page => this.page.next(page))
            .handle(SystemError, () => this.systemError('Error in loadPage: Internal System Error'))
            .handle(UnsupportedDate, () => this.showQueryError('WorkItemSearchBadDate'))
            .handle(UnsupportedWildcardLocation, () => this.showQueryError('WorkItemSearchBadWildcard'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Error in loadPage: ${errorThrown}`, textStatus))
            .final(() => final())
            .post(this.csrfToken());
    }

    private showHistory($target: JQuery, workflowInstanceId: string): void {
        Request.create(this.serializer, path.api(`workflowstatus/history/${workflowInstanceId}`))
            .before(() => this.updating())
            .handleArray(WorkItemInfo, history => this.showWorkflowStatusHistory($target, history))
            .handle(null, history => this.showWorkflowStatusHistory($target, []))
            .handle(SystemError, () => this.systemError('System Error : Search.showWorkflowStatusHistory.get'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Http Error : Search.showWorkflowStatusHistory.get : ${errorThrown}`, textStatus))
            .final(() => this.notUpdating())
            .post(this.csrfToken());
    }

    private performSearch(): void {
        // Cancel any existing search
        this.currentSearchDisposable.set(null);
        
        this.updating(this.$viewTable);

        // update the header if the result type has changed
        var searchTypeInfo = this.determineSearchTypeInfo(this.vm.version());
        // re-paint the view header each search type group change to clear the related column sizes
        var viewResultType = searchTypeInfo.isWorkflowStatusSearch ? ViewResultType.SearchWorkflowStatus : ViewResultType.SearchWorkItems;
        if (this.viewResultType !== viewResultType) {
            this.paintViewHeader(this.view.getValue(), viewResultType);
        }

        var args: IViewLoadPageArgs = {
            viewId: this.view.getValue().id,
            pageSize: this.pageSize.getValue(),
            pageIndex: this.firstPageIndex,
            sortDescriptions: this.sortDescriptions.getValue()
        };
        
        // Track the current search operation
        const searchRequest = this.loadPage(args, () => this.notUpdating());
        
        this.currentSearchDisposable.set(disposableSubscription(searchRequest));
    }

    protected onViewChanged(view: WorkTrayView, final: () => void): void {
        var searchTypeInfo = this.determineSearchTypeInfo(this.vm.version());
        var viewResultType = searchTypeInfo.isWorkflowStatusSearch ? ViewResultType.SearchWorkflowStatus : ViewResultType.SearchWorkItems;

        this.paintViewHeader(view, viewResultType);

        var args: IViewLoadPageArgs = {
            viewId: view.id,
            pageSize: this.pageSize.getValue(),
            pageIndex: this.firstPageIndex,
            sortDescriptions: this.sortDescriptions.getValue()
        };

        this.loadPage(args, final);
    }

    protected reloadPage(args: IViewLoadPageArgs, final: () => void): void {
        this.loadPage(args, final);
    }

    protected paintViewBodyVirtual(view: WorkTrayView, page: SearchResultPage | WorkflowInstanceInfoPage): void {
        var displayHistory = page instanceof WorkflowInstanceInfoPage;

        this.paintViewBody(displayHistory, false/*displayActionMenu*/, page);

        this.showNoResults(page.totalItemCount === 0, this.lastSearchQuery);
    }

    private showNoResults(show: boolean, query?: string): void {
        this.$viewTable.children('.wf-search-noresults').remove();

        if (!show)
            return;

        const trimmed = (query || '').trim();
        const $title = $('<div />').addClass('wf-search-noresults-title');

        if (trimmed) {
            // the query is styled rather than quoted: the search syntax itself uses double quotes for phrases
            const [before, after] = this.resources.getCoreString('WorkItemSearchNoResultsForQuery').split('{0}');
            $title.append([
                document.createTextNode(before),
                $('<span />').addClass('wf-search-noresults-query').text(trimmed),
                document.createTextNode(after || '')
            ]);
        } else
            $title.text(this.resources.getCoreString('NoResults'));

        const $noResults = $('<div />').addClass('wf-search-noresults').append($title);

        if (trimmed)
            $noResults.append(
                $('<div />').addClass('wf-search-noresults-hint').append([
                    document.createTextNode(this.resources.getCoreString('WorkItemSearchNoResultsHint') + ' '),
                    $('<a />').addClass('wf-search-tips-link').attr('href', '#')
                        .text(this.resources.getCoreString('WorkItemSearchSyntaxHintTitle'))
                        .on('click', e => { e.preventDefault(); this.toggleSearchTips(true); })
                ])
            );

        $noResults.appendTo(this.$viewTable);
    }

    // a rejected query must not leave the previous search's rows or pager on screen under the toast
    protected showQueryError(resourceKey: string) {
        alerts.error(this.resources.getCoreString(resourceKey));
        this.clearViewBody();
    }
}

const dateValueFormat = dateTimeTransmissionFormat();

class SearchViewModel implements IViewModelRoot {
    eventAggregator: IEventAggregator;
    resources: IResourceProvider;

    searchQuery = ko.observable<string>();

    fromDate = ko.observable<string>();
    toDate = ko.observable<string>();

    version = ko.observable<string>();

    private versionOptions: LookupItem[];

    constructor(eventAggregator: IEventAggregator, resources: IResourceProvider) {
        this.eventAggregator = eventAggregator;
        this.resources = resources;

        this.versionOptions = ['LatestVersionOnly', 'AllVersions', 'StatusLatest', 'StatusAllVersions']
            .map(x => ({
                keyValue: x,
                displayValue: resources.getCoreString(`${x}Text`),
                parentKeyValue: null,
                properties: [],
                active: true
            }));

        this.reset();

        this.fromDate.subscribe(newValue => {
            if (newValue === undefined || newValue === null) {
                this.resetFromDate();
            }
        });
        this.toDate.subscribe(newValue => {
            if (newValue === undefined || newValue === null) {
                this.resetToDate();
            }
        });
        this.version.subscribe(newValue => {
            if (newValue === undefined || newValue === null) {
                this.resetVersion();
            }
        });
    }

    private resetFromDate(): void {
        var fromDateValue = moment().startOf('day').format(dateValueFormat);
        this.fromDate(fromDateValue);
    }

    private resetToDate(): void {
        var toDateValue = moment().endOf('day').format(dateValueFormat);
        this.toDate(toDateValue);
    }

    private resetVersion(): void {
        this.version(this.versionOptions[3].keyValue); //StatusAllVersions
    }

    search(): void {
        this.eventAggregator.publish(new SearchEvent());
    }

    reset(): void {
        this.searchQuery(null);

        this.resetFromDate();
        this.resetToDate();

        this.resetVersion();
    }

    getCriteria(): ISearchCriteria {
        return <any>komapping.toJS(this, { ignore: ['eventAggregator', 'resources', 'search', 'reset', 'getCriteria', 'versionOptions', 'getVersions', 'getVersion'] });
    }

    getVersions(): LookupItem[] {
        return this.versionOptions;
    }

    getVersion(keyValue: string): LookupItem {
        return this.versionOptions.filter(v => v.keyValue === keyValue)[0];
    }
}